import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { AnimatePresence, motion } from "motion/react";
import { CheckCircle2, Loader2, RefreshCcw, WifiOff } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/utils/cn";
import { socketManager } from "@/services/websocket/socket-manager";
import { selectConnectionStatus } from "@/store/slices/connection-status.slice";

const RECONNECTING_GRACE_MS = 1200;
const RECONNECTED_FLASH_MS = 2500;

type BarView = "hidden" | "reconnecting" | "disconnected" | "reconnected";

type ToneMap = {
  ring: string;
  iconWrap: string;
  title: string;
  pulse: string;
};

const TONES: Record<Exclude<BarView, "hidden">, ToneMap> = {
  disconnected: {
    ring: "ring-danger/25",
    iconWrap: "bg-danger/12 text-danger",
    title: "text-danger",
    pulse: "bg-danger",
  },
  reconnecting: {
    ring: "ring-warning/25",
    iconWrap: "bg-warning/15 text-warning",
    title: "text-warning",
    pulse: "bg-warning",
  },
  reconnected: {
    ring: "ring-success/25",
    iconWrap: "bg-success/12 text-success",
    title: "text-success",
    pulse: "bg-success",
  },
};

const SocketStatusBanner = () => {
  const { t } = useTranslation("common");
  const status = useSelector(selectConnectionStatus);
  const [view, setView] = useState<BarView>("hidden");
  const wasDownRef = useRef(false);

  useEffect(() => {
    if (status === "disconnected") {
      wasDownRef.current = true;
      setView("disconnected");
      return;
    }
    if (status === "reconnecting") {
      wasDownRef.current = true;
      const t = setTimeout(
        () => setView("reconnecting"),
        RECONNECTING_GRACE_MS,
      );
      return () => clearTimeout(t);
    }
    if (wasDownRef.current) {
      wasDownRef.current = false;
      setView("reconnected");
      const t = setTimeout(() => setView("hidden"), RECONNECTED_FLASH_MS);
      return () => clearTimeout(t);
    }
    setView("hidden");
  }, [status]);

  return (
    <AnimatePresence>
      {view !== "hidden" && (
        <motion.div
          key="socket-status"
          role="status"
          aria-live="polite"
          initial={{ opacity: 0, y: 32, x: "-50%", scale: 0.92 }}
          animate={{ opacity: 1, y: 0, x: "-50%", scale: 1 }}
          exit={{ opacity: 0, y: 32, x: "-50%", scale: 0.92 }}
          transition={{ type: "spring", stiffness: 420, damping: 32 }}
          className={cn(
            "fixed bottom-6 left-1/2 z-[9999] whitespace-nowrap",
            "flex items-center gap-3 rounded-2xl border border-border/60 bg-surface/90 py-2 ps-2 pe-2.5",
            "shadow-2xl ring-1 backdrop-blur-md",
            TONES[view].ring,
          )}
        >
          {/* Status icon */}
          <div
            className={cn(
              "relative flex size-9 shrink-0 items-center justify-center rounded-2xl",
              TONES[view].iconWrap,
            )}
          >
            {(view === "disconnected" || view === "reconnecting") && (
              <span
                className={cn(
                  "absolute inset-0 rounded-2xl opacity-40 motion-safe:animate-ping",
                  TONES[view].pulse,
                )}
                aria-hidden="true"
              />
            )}
            <span className="relative">
              {view === "reconnecting" && (
                <Loader2 className="size-4 animate-spin" aria-hidden="true" />
              )}
              {view === "disconnected" && (
                <WifiOff className="size-4" aria-hidden="true" />
              )}
              {view === "reconnected" && (
                <CheckCircle2 className="size-4" aria-hidden="true" />
              )}
            </span>
          </div>

          {/* Title + subtitle stacked */}
          <div className="flex flex-col leading-tight pe-1">
            <span
              className={cn("text-[13px] font-semibold", TONES[view].title)}
            >
              {view === "reconnecting" &&
                t("common:socket_status.reconnecting")}
              {view === "disconnected" &&
                t("common:socket_status.connection_lost")}
              {view === "reconnected" && t("common:socket_status.back_online")}
            </span>
            <span className="text-[11px] text-muted-foreground">
              {view === "reconnecting" &&
                t("common:socket_status.restoring_live_updates")}
              {view === "disconnected" &&
                t("common:socket_status.live_updates_paused")}
              {view === "reconnected" &&
                t("common:socket_status.live_updates_resumed")}
            </span>
          </div>

          {/* Retry */}
          {view === "disconnected" && (
            <Button
              size="xs"
              variant="danger"
              className="h-7! rounded-xl px-3! text-[11px]!"
              onClick={() => socketManager.reconnectAll()}
            >
              <RefreshCcw className="size-3" strokeWidth={2.25} />
              {t("buttons.retry")}
            </Button>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SocketStatusBanner;
